import { Box, Typography } from '@mui/material';
import DeformIcon from '~/assets/tool-icons/deform.svg?react';
import LineDeformIcon from '~/assets/tool-icons/line_deform.svg?react';
import SmoothBrushIcon from '~/assets/tool-icons/smooth_brush.svg?react';
import CrossSectionIcon from '~/assets/tool-icons/cross-section.svg?react';
import { enqueueSnackbar } from '~/utils/notifications';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useRecoilState, useSetRecoilState } from 'recoil';
import { useIntl } from 'react-intl';
import { editBlockedReasonId } from '~/components/Structures/editBlockedMessage';
import {
    AdaptiveBrushIcon,
    BrushIcon,
    EraseIcon,
    LineIcon,
    TransformIcon,
} from '~/assets/workspace-icons';
import ToolbarButton from '~/components/toolbar/shared/ToolbarButton';
import ToolGroup from '~/components/toolbar/shared/ToolGroup';
import ToolPropertiesTray from '~/components/toolbar/shared/ToolPropertiesTray';
import { buildToolTooltip } from '~/components/toolbar/shared/toolTooltip';
import {
    isParkedContouringTool,
    useContouringToolsParked,
} from '~/components/toolbar/shared/useParkedContouringTool';
import { useStructureToolGating } from '~/hooks/target/useStructureToolGating';
import { RoiInterpretedType } from '~/domain/dicom/dicomstructureset';
import { findRoisByInterpretedType } from '~/domain/readmodels/StructureSetReadModel';
import { useContourAutoSuggestionController } from '~/hooks/useContourAutoSuggestionController';

import {
    brushOptionsState,
    lineOptionsState,
    selectedToolState,
    contourAutoSuggestionEnabledState,
    Tools,
} from '~/state/atoms/toolsAtoms';
import DrawingConstraintsControl from './modifiers/DrawingConstraintsControl';
import BrushToolOptions from './BrushToolOptions';
import DeformToolOptions from './DeformToolOptions';
import LineDeformToolOptions from './LineDeformToolOptions';
import LineToolOptions from './LineToolOptions';
import EraserToolOptions from './EraserToolOptions';
import SmoothBrushToolOptions from './SmoothBrushToolOptions';
import CrossSectionPreviewHost from './crossSection/CrossSectionPreviewHost';
import CrossSectionToolOptions from './crossSection/CrossSectionToolOptions';
import TransformToolOptions from './TransformToolOptions';

/** Content width of the Cross-Section hint, which opens under its button and runs rightwards from the button's left edge. */
const CROSS_SECTION_HINT_MAX_WIDTH_PX = 224;

export default function ContouringTools() {
    const intl = useIntl();
    const [tool, setTool] = useRecoilState(selectedToolState);
    const {
        selectedStructureSet,
        selectedRoiNumber,
        selectedRoi,
        isPointRoi,
        isTilted,
        selectionEditBlocker,
        isBulkSelectionActive,
        hasSelectedRoi,
        selectedRoiAnyPlaneEditDisabled,
        selectedRoiContourEditDisabled,
        targetUnresolved,
        isPrimaryImageIncomplete,
        imageIncompleteReason,
    } = useStructureToolGating();
    const [brushOptions, setBrushOptions] = useRecoilState(brushOptionsState);
    const [lineOptions, setLineOptions] = useRecoilState(lineOptionsState);
    const setAutoSuggestEnabled = useSetRecoilState(
        contourAutoSuggestionEnabledState
    );
    const [validationError, setValidationError] = useState<string | null>(null);

    useContourAutoSuggestionController();

    const noRoiSelected = !hasSelectedRoi;

    const isContouringToolsDisabled = selectedRoiContourEditDisabled;
    const isAnyPlaneToolsDisabled = selectedRoiAnyPlaneEditDisabled;

    const isMoveTransformDisabled =
        noRoiSelected ||
        isTilted ||
        selectionEditBlocker !== undefined ||
        isBulkSelectionActive ||
        // Transform/move POI writes contour geometry just as the brush does.
        isPrimaryImageIncomplete;

    /**
     * When a contouring tool is disabled, return a short reason
     * that can be appended to the tooltip so the user knows why.
     * Returns undefined when the tool isn't disabled (caller falls
     * back to the plain tool name). Tools that edit on any plane
     * are not gated by a tilted grid, so that reason is skipped for
     * them.
     */
    const disabledReasonFor = useCallback(
        (gatedByTiltedGrid: boolean): string | undefined => {
            // Checked first: targetUnresolved also drives isTilted
            // conservatively true (see useStructureToolGating), so it must
            // win over the oblique reason below or the tooltip would name
            // the wrong cause.
            if (targetUnresolved) {
                return intl.formatMessage({
                    id: 'tool.disabled.gridTargetUnresolved',
                });
            }
            // Next, because it is the one condition the user cannot act on
            // at all: no selection change fixes an image that is still
            // arriving.
            if (isPrimaryImageIncomplete) {
                return imageIncompleteReason;
            }
            if (isBulkSelectionActive) {
                return intl.formatMessage({
                    id: 'tool.disabled.bulkSelection',
                });
            }
            if (selectionEditBlocker) {
                return intl.formatMessage({
                    id: editBlockedReasonId(selectionEditBlocker),
                });
            }
            if (gatedByTiltedGrid && isTilted) {
                return intl.formatMessage({ id: 'tool.disabled.oblique' });
            }
            if (isPointRoi) {
                return intl.formatMessage({ id: 'tool.disabled.pointRoi' });
            }
            if (noRoiSelected) {
                return intl.formatMessage({ id: 'tool.disabled.noRoi' });
            }
            return undefined;
        },
        [
            intl,
            targetUnresolved,
            selectionEditBlocker,
            isTilted,
            isPointRoi,
            noRoiSelected,
            isBulkSelectionActive,
            isPrimaryImageIncomplete,
            imageIncompleteReason,
        ]
    );
    const disabledReason = useMemo(
        () => disabledReasonFor(true),
        [disabledReasonFor]
    );
    const anyPlaneDisabledReason = useMemo(
        () => disabledReasonFor(false),
        [disabledReasonFor]
    );

    // A box on screen parks the tools that write to the ROI. They
    // must READ as unavailable for as long as it lasts: a button that looks
    // armed and does nothing is the same lie as a toggle reading on with no
    // tool armed. Which tools those are is asked of the parking hook, never
    // re-listed here — a divergence would leave a parked tool looking live.
    const isParked = useContouringToolsParked();
    const parkedFor = useCallback(
        (armedTool: Tools) => isParked && isParkedContouringTool(armedTool),
        [isParked]
    );
    // Parking comes LAST: it is transient, and stating it over a standing
    // reason (approved, no ROI) would send the user to close a box that was
    // never what stopped them.
    //
    // `standingReason` is the gate the tool answers to — the tilted-grid one
    // for tools that need an image slice plane, the any-plane one for those
    // that do not. `extraReason` is a tool's OWN standing refusal (today: the
    // rectangle erase's geometry); it sits after the shared reason and before
    // parking, for the same reason parking is last.
    const reasonFor = (
        armedTool: Tools,
        standingReason: string | undefined,
        extraReason?: string
    ): string | undefined =>
        standingReason ??
        extraReason ??
        (parkedFor(armedTool)
            ? intl.formatMessage({ id: 'tool.disabled.regionBox' })
            : undefined);

    const tooltipFor = (
        toolName: string,
        armedTool: Tools,
        extraReason?: string
    ) =>
        buildToolTooltip(
            intl,
            toolName,
            reasonFor(armedTool, disabledReason, extraReason)
        );

    // The brushes, the eraser, deform and the smoothing brush edit on any
    // plane, so they answer to the any-plane gate rather than the
    // tilted-grid one — but a region box parks them like every other tool
    // that writes to the ROI.
    const anyPlaneTooltipFor = (toolName: string, armedTool: Tools) =>
        buildToolTooltip(
            intl,
            toolName,
            reasonFor(armedTool, anyPlaneDisabledReason)
        );

    /** A tool tooltip's body: the tool's name over a dimmer usage hint, at most `maxWidthPx` wide. */
    const tooltipWithHint = (
        toolName: string,
        hint: string,
        maxWidthPx?: number
    ) => (
        <Box sx={{ maxWidth: maxWidthPx }}>
            <Typography fontSize={11}>{toolName}</Typography>
            <Typography component="span" sx={{ fontSize: 10, opacity: 0.7 }}>
                {hint}
            </Typography>
        </Box>
    );

    const isStaticBrushSelected =
        tool === 'brush' &&
        brushOptions.mode === 'static' &&
        !brushOptions.erase;
    const isAdaptiveBrushSelected =
        tool === 'brush' &&
        brushOptions.mode === 'smart' &&
        !brushOptions.erase;
    const isLineDrawSelected = tool === 'line' && lineOptions.mode === 'draw';
    // Highlight follows tool selection (draw or erase); the "Erase" toggle is
    // the mode indicator. Click handling keys off draw mode via isLineDrawSelected.
    const isLineSelected = tool === 'line';
    // The eraser is one tool with two shapes: the erasing brush and the
    // rectangle that takes whole curves. Both keep this button lit, so
    // switching shape inside the tray does not read as leaving the tool.
    const isEraseSelected =
        (tool === 'brush' && brushOptions.erase) || tool === 'rectangle-select';
    const isTransformSelected = tool === 'transform' || tool === 'poi-move';
    const isLineDeformSelected = tool === 'lineDeform';
    const isCrossSectionSelected = tool === 'cross-section';
    const isCrossSectionDisabled =
        isAnyPlaneToolsDisabled || parkedFor('cross-section');

    useEffect(() => {
        if (
            selectedRoiNumber === -1 ||
            selectionEditBlocker !== undefined ||
            isBulkSelectionActive ||
            // With no editable target every tool returns to pan, as for the
            // other terms; a button left lit would claim a tool that is not
            // armed.
            targetUnresolved ||
            // Switching the primary image to one that is still streaming must
            // disarm an already-armed mutating tool, not just grey its button.
            isPrimaryImageIncomplete
        ) {
            setTool('pan');
            return;
        }

        setTool((prev) => {
            if (isPointRoi) {
                if (prev === 'transform' || prev === 'poi-move') {
                    return 'poi-move';
                }
                return 'pan';
            } else {
                if (prev === 'poi-move') {
                    return 'transform';
                }
            }
            return prev;
        });
    }, [
        selectedRoiNumber,
        isPointRoi,
        selectionEditBlocker,
        isBulkSelectionActive,
        targetUnresolved,
        isPrimaryImageIncomplete,
        setTool,
    ]);

    // A write tool selected while the image is incomplete (a box restoring
    // the tool it parked) returns to pan as well; a viewing tool stays.
    useEffect(() => {
        if (
            isPrimaryImageIncomplete &&
            (isParkedContouringTool(tool) || tool === 'poi-move')
        ) {
            setTool('pan');
        }
    }, [tool, isPrimaryImageIncomplete, setTool]);

    useEffect(() => {
        if (validationError) {
            enqueueSnackbar(validationError, { variant: 'error' });
            setValidationError(null);
        }
    }, [validationError]);

    useEffect(() => {
        // The auto-suggest toggle persists across tool switches —
        // turning it on while in brush mode keeps it on when the
        // user switches to line / line-deform / etc. so they don't
        // have to re-enable the feature each time. We only force it
        // off when contouring as a whole is unavailable for the
        // current selection (no ROI, point ROI, approved structure, or a
        // structure set on no grid row), since the controller has nothing
        // to operate on in those states. Whether the displayed plane can
        // carry a suggestion is a property of the view, not the selection,
        // and is stated on the switch itself.
        if (isAnyPlaneToolsDisabled) {
            setAutoSuggestEnabled(false);
        }
    }, [isAnyPlaneToolsDisabled, setAutoSuggestEnabled]);

    const handleStaticBrushSelect = () => {
        if (isStaticBrushSelected) {
            setTool('pan');
            return;
        }
        setTool('brush');
        setBrushOptions((options) => ({
            ...options,
            mode: 'static',
            erase: false,
        }));
    };

    const handleAdaptiveBrushSelect = () => {
        if (isAdaptiveBrushSelected) {
            setTool('pan');
            return;
        }
        setTool('brush');
        setBrushOptions((options) => ({
            ...options,
            mode: 'smart',
            erase: false,
        }));
    };

    const handleLineSelect = () => {
        if (isLineDrawSelected) {
            setTool('pan');
            return;
        }
        if (isTilted) {
            setValidationError(
                'Line draw tool is not available for tilted datasets'
            );
            return;
        }
        setTool('line');
        setLineOptions((options) => ({ ...options, mode: 'draw' }));
    };

    const handleEraseSelect = () => {
        if (isEraseSelected) {
            setTool('pan');
            return;
        }
        // Always opens on the brush shape: the rectangle is reached from
        // the tray, so arming the eraser cannot land the user in a
        // gesture they did not pick.
        setTool('brush');
        setBrushOptions((options) => ({
            ...options,
            mode: 'static', // Erase mode uses the same brush behavior as static, just with the erase flag on
            erase: true,
        }));
    };

    // The one button arms two different tools, and only one of them is
    // parked: a POI move cannot reshape a contour, so the button stays live
    // for a point ROI even with a box on screen.
    const transformTool: Tools = isPointRoi ? 'poi-move' : 'transform';
    // Whether the Transform tooltip states why the button is unavailable.
    const transformStatesReason =
        (isMoveTransformDisabled && !!disabledReason) ||
        parkedFor(transformTool);

    const handleTransformSelect = useCallback(() => {
        if (isTransformSelected) {
            setTool('pan');
            return;
        }
        setTool(transformTool);
    }, [setTool, transformTool, isTransformSelected]);

    const handleLineDeformSelect = () => {
        if (isLineDeformSelected) {
            setTool('pan');
            return;
        }
        if (isTilted) {
            setValidationError(
                'Line deform tool is not available for tilted datasets'
            );
            return;
        }
        setTool('lineDeform');
    };

    const handleDeformSelect = () => {
        if (tool === 'deform') {
            setTool('pan');
            return;
        }
        setTool('deform');
    };

    const handleSmoothBrushSelect = () => {
        if (tool === 'smoothBrush') {
            setTool('pan');
            return;
        }
        setTool('smoothBrush');
    };

    const handleCrossSectionSelect = () => {
        if (isCrossSectionSelected) {
            setTool('pan');
            return;
        }
        setTool('cross-section');
    };

    const isSelectedSupport =
        selectedRoi?.interpretedType === RoiInterpretedType.SUPPORT;
    const hasSiblingSupport = useMemo(() => {
        if (!isSelectedSupport || !selectedStructureSet) {
            return false;
        }
        return (
            findRoisByInterpretedType(
                selectedStructureSet.roiArray,
                RoiInterpretedType.SUPPORT,
                selectedRoiNumber
            ).length > 0
        );
    }, [isSelectedSupport, selectedStructureSet, selectedRoiNumber]);

    // Erase spans two tools — brush erase and line erase. Neither applies the
    // Inside/Outside constraints (the draw action short-circuits them for erase
    // strokes), so the popover annotates the sections as dormant in both.
    const isErasing =
        isEraseSelected || (tool === 'line' && lineOptions.mode === 'erase');

    const constraintsSection = (
        <DrawingConstraintsControl
            disabled={isAnyPlaneToolsDisabled}
            erasing={isErasing}
            // Auto-suggest is honoured only for the brush in a draw mode; it is
            // dead for the line tray and while erasing (see
            // useContourAutoSuggestionController), so only surface it there.
            showAutoSuggest={tool === 'brush' && !isErasing}
        />
    );

    return (
        <>
            <ToolGroup accent>
                <ToolbarButton
                    icon={<BrushIcon />}
                    tooltip={anyPlaneTooltipFor('Static Brush', 'brush')}
                    selected={isStaticBrushSelected}
                    disabled={isAnyPlaneToolsDisabled || parkedFor('brush')}
                    onClick={handleStaticBrushSelect}
                    data-testid="tool-static-brush"
                />

                <ToolbarButton
                    icon={<AdaptiveBrushIcon />}
                    tooltip={anyPlaneTooltipFor('Adaptive Brush', 'brush')}
                    selected={isAdaptiveBrushSelected}
                    disabled={isAnyPlaneToolsDisabled || parkedFor('brush')}
                    onClick={handleAdaptiveBrushSelect}
                    data-testid="tool-adaptive-brush"
                />

                <ToolbarButton
                    icon={<LineIcon />}
                    tooltip={tooltipFor('Line Draw', 'line')}
                    selected={isLineSelected}
                    disabled={isContouringToolsDisabled || parkedFor('line')}
                    onClick={handleLineSelect}
                    data-testid="tool-line-draw"
                />

                <ToolbarButton
                    icon={<EraseIcon />}
                    tooltip={anyPlaneTooltipFor('Erase', 'brush')}
                    selected={isEraseSelected}
                    disabled={isAnyPlaneToolsDisabled || parkedFor('brush')}
                    onClick={handleEraseSelect}
                    data-testid="tool-erase"
                />
                <ToolbarButton
                    icon={<TransformIcon />}
                    tooltip={
                        transformStatesReason
                            ? tooltipFor(
                                  isPointRoi
                                      ? 'Move POI'
                                      : 'Transform Structure',
                                  transformTool
                              )
                            : isPointRoi
                              ? 'Move POI'
                              : tooltipWithHint(
                                    'Transform Structure',
                                    'drag to move, structure edge to scale'
                                )
                    }
                    // The string tooltips name the button themselves; the
                    // two-line one does not.
                    ariaLabel={
                        transformStatesReason || isPointRoi
                            ? undefined
                            : 'Transform Structure'
                    }
                    selected={isTransformSelected}
                    disabled={
                        isMoveTransformDisabled || parkedFor(transformTool)
                    }
                    onClick={handleTransformSelect}
                    data-testid="tool-transform"
                />
                <ToolbarButton
                    icon={<LineDeformIcon />}
                    tooltip={tooltipFor('Line deform', 'lineDeform')}
                    selected={isLineDeformSelected}
                    disabled={
                        isContouringToolsDisabled || parkedFor('lineDeform')
                    }
                    onClick={handleLineDeformSelect}
                    data-testid="tool-line-deform"
                />
                <ToolbarButton
                    icon={<DeformIcon />}
                    tooltip={anyPlaneTooltipFor('Deform', 'deform')}
                    selected={tool === 'deform'}
                    disabled={isAnyPlaneToolsDisabled || parkedFor('deform')}
                    onClick={handleDeformSelect}
                    data-testid="tool-deform"
                />
                <ToolbarButton
                    icon={<SmoothBrushIcon />}
                    tooltip={anyPlaneTooltipFor(
                        'Smoothing Brush',
                        'smoothBrush'
                    )}
                    selected={tool === 'smoothBrush'}
                    disabled={
                        isAnyPlaneToolsDisabled || parkedFor('smoothBrush')
                    }
                    onClick={handleSmoothBrushSelect}
                    data-testid="tool-smooth-brush"
                />
                <ToolbarButton
                    icon={<CrossSectionIcon />}
                    tooltip={
                        isCrossSectionDisabled
                            ? anyPlaneTooltipFor(
                                  'Cross-Section',
                                  'cross-section'
                              )
                            : tooltipWithHint(
                                  'Cross-Section',
                                  intl.formatMessage({
                                      id: 'crossSectionTool.hint',
                                  }),
                                  CROSS_SECTION_HINT_MAX_WIDTH_PX
                              )
                    }
                    // The string tooltip names the button itself; the
                    // two-line one does not.
                    ariaLabel={
                        isCrossSectionDisabled ? undefined : 'Cross-Section'
                    }
                    tooltipPlacement="bottom-start"
                    selected={isCrossSectionSelected}
                    disabled={isCrossSectionDisabled}
                    onClick={handleCrossSectionSelect}
                    data-testid="tool-cross-section"
                />
            </ToolGroup>
            {tool === 'brush' && !brushOptions.erase && (
                <ToolPropertiesTray>
                    <BrushToolOptions />
                    {constraintsSection}
                </ToolPropertiesTray>
            )}
            {isEraseSelected && (
                <ToolPropertiesTray>
                    <EraserToolOptions />
                </ToolPropertiesTray>
            )}
            {tool === 'line' && (
                <ToolPropertiesTray>
                    <LineToolOptions />
                    {constraintsSection}
                </ToolPropertiesTray>
            )}
            {tool === 'lineDeform' && (
                <ToolPropertiesTray>
                    <LineDeformToolOptions />
                </ToolPropertiesTray>
            )}
            {tool === 'deform' && (
                <ToolPropertiesTray>
                    <DeformToolOptions />
                </ToolPropertiesTray>
            )}
            {tool === 'smoothBrush' && (
                <ToolPropertiesTray>
                    <SmoothBrushToolOptions />
                </ToolPropertiesTray>
            )}
            {tool === 'transform' && isSelectedSupport && hasSiblingSupport && (
                <ToolPropertiesTray>
                    <TransformToolOptions />
                </ToolPropertiesTray>
            )}
            {tool === 'cross-section' && (
                <>
                    <CrossSectionPreviewHost />
                    <ToolPropertiesTray>
                        <CrossSectionToolOptions />
                        {constraintsSection}
                    </ToolPropertiesTray>
                </>
            )}
        </>
    );
}
